import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useModelFormStore } from '../../stores/ModelFormStore';
import { useCaseRowStore } from '../../stores/CaseRowStore';
import { useAddModelStore } from '../../pages/AddModel/stores/AddModelStore';
import { globalLinks } from './links/globalLinks';
import { useModelLinks } from './links/useModelLinks';
import { getSimulationLinks } from './links/simulationLinks';
import type { SidebarConfig } from './types';

type RouteContext = 'global' | 'model' | 'simulation' | 'addModel';

function useRouteContext(): {
  context: RouteContext;
  modelId?: string;
  simulationId?: string;
} {
  const location = useLocation();
  const { modelId, simulationId } = useParams<{
    modelId: string;
    simulationId: string;
  }>();

  if (location.pathname === '/add-model') {
    return { context: 'addModel' };
  }

  if (/^\/simulations\/[^/]+/.test(location.pathname)) {
    return { context: 'simulation', simulationId };
  }

  if (
    !!modelId &&
    /^\/models\/[^/]+/.test(location.pathname) &&
    location.pathname !== '/models'
  ) {
    return { context: 'model', modelId };
  }

  return { context: 'global' };
}

export function useSidebarConfig(): SidebarConfig {
  const { context, modelId, simulationId } = useRouteContext();
  const navigate = useNavigate();

  const { setAnalogueModelDefault } = useModelFormStore(
    useShallow((s) => ({
      setAnalogueModelDefault: s.setAnalogueModelDefault,
    })),
  );
  const { resetStates } = useCaseRowStore();
  const uploading = useAddModelStore((s) => s.uploading);
  const resetAddModel = useAddModelStore((s) => s.resetState);

  const modelLinks = useModelLinks(context === 'model' ? modelId : undefined);

  switch (context) {
    case 'model':
      return {
        links: modelLinks,
        backAction: {
          label: 'Back to models',
          onBack: () => {
            setAnalogueModelDefault();
            resetStates();
            navigate('/models');
          },
        },
      };

    case 'simulation':
      return {
        links: getSimulationLinks(simulationId ?? ''),
        backAction: {
          label: 'Back to simulations',
          onBack: () => navigate('/models?tab=simulations'),
        },
      };

    case 'addModel':
      return {
        links: [],
        backAction: {
          label: 'Back to models',
          disabledTooltip: uploading
            ? 'Button disabled until model has finished uploading.'
            : undefined,
          onBack: () => {
            setAnalogueModelDefault();
            resetAddModel();
            navigate('/models');
          },
        },
      };

    case 'global':
    default:
      return { links: globalLinks };
  }
}
